(() => {
  'use strict';
  if (customElements.get('ruemood-homepage')) return;

  // The campaign clock measures visible reading time. No catalog, bag, or analytics store lives here.
  function campaign(root, signal) {
    if (!root) return () => {};
    const viewport = root.querySelector('.hero-viewport');
    const slides = [...viewport.querySelectorAll('.campaign')];
    const dots = [...root.querySelectorAll('[data-hero-dot]')];
    const status = root.querySelector('.hero-status');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const duration = Number(root.dataset.heroInterval) || 8000;
    const listen = (el, type, fn, options = {}) => el.addEventListener(type, fn, {...options, signal});
    let current = 0, settleTimer, width = viewport.clientWidth, visible = !('IntersectionObserver' in window);
    let elapsed = 0, lastTick = null, frame = 0;
    let focused = false, hovered = false, interacting = false, scrolling = false;
    const progress = () => root.style.setProperty('--hero-progress', String(Math.min(1, elapsed / duration)));
    const reset = () => { elapsed = 0; lastTick = null; progress(); };
    function playback() {
      const playing = !signal.aborted && !focused && !reduced.matches && visible && !document.hidden &&
        !hovered && !interacting && !scrolling && !document.querySelector('dialog[open]');
      root.dataset.heroPlaying = String(playing);
      if (playing && !frame) frame = requestAnimationFrame(tick);
      if (!playing) { cancelAnimationFrame(frame); frame = 0; lastTick = null; }
    }
    function tick(now) {
      frame = 0;
      if (lastTick !== null) elapsed += now - lastTick;
      lastTick = now; progress();
      if (elapsed >= duration) go((current + 1) % slides.length, 'auto');
      else frame = requestAnimationFrame(tick);
    }
    function select(index, announce = true) {
      current = index; root.dataset.heroCurrent = String(current);
      dots.forEach((dot, i) => { if (i === current) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      slides.forEach((slide, i) => { slide.inert = i !== current; slide.setAttribute('aria-hidden', String(i !== current)); });
      reset(); status.textContent = announce ? slides[current].getAttribute('aria-label') : '';
    }
    function go(index, source) {
      index = (index + slides.length) % slides.length;
      if (index === current) { reset(); playback(); return; }
      const wrapping = source === 'auto' && current === slides.length - 1 && index === 0;
      select(index, source !== 'auto'); scrolling = true; playback();
      if (wrapping && !reduced.matches && viewport.animate) viewport.animate([{opacity: .55}, {opacity: 1}], {duration: 350, easing: 'ease-out'});
      viewport.scrollTo({left: current * viewport.clientWidth, behavior: reduced.matches || wrapping ? 'instant' : 'smooth'});
      clearTimeout(settleTimer); settleTimer = setTimeout(settled, 180);
    }
    function settled() {
      clearTimeout(settleTimer);
      if (interacting || !viewport.clientWidth) return;
      const index = Math.max(0, Math.min(slides.length - 1, Math.round(viewport.scrollLeft / viewport.clientWidth)));
      if (index !== current) {
        if (slides[current].contains(document.activeElement)) dots[index].focus({preventScroll: true});
        select(index);
      }
      scrolling = false; playback();
    }
    listen(root, 'click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.dataset.heroDot !== undefined) go(Number(button.dataset.heroDot), 'dot');
      if (button.dataset.heroDirection) go(current + Number(button.dataset.heroDirection), 'arrow');
    });
    listen(root, 'keydown', event => {
      focused = true; playback();
      const dot = event.target.closest('[data-hero-dot]');
      if (!dot) return;
      let index = Number(dot.dataset.heroDot);
      if (event.key === 'ArrowRight') index = (index + 1) % slides.length;
      else if (event.key === 'ArrowLeft') index = (index + slides.length - 1) % slides.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = slides.length - 1;
      else return;
      event.preventDefault(); dots[index].focus({preventScroll: true}); go(index, 'keyboard');
    });
    const hasKeyboardFocus = () => root.contains(document.activeElement) && Boolean(document.activeElement?.matches(':focus-visible'));
    listen(root, 'focusin', () => { focused = hasKeyboardFocus(); playback(); });
    listen(root, 'focusout', () => queueMicrotask(() => { focused = hasKeyboardFocus(); if (!focused) reset(); playback(); }));
    const overAction = target => { const action = target?.closest?.('button,a'); return Boolean(action && root.contains(action)); };
    listen(root, 'pointerover', e => { if (e.pointerType === 'mouse') { hovered = overAction(e.target); playback(); } });
    listen(root, 'pointerout', e => { if (e.pointerType === 'mouse') { hovered = overAction(e.relatedTarget); playback(); } });
    listen(root, 'pointerleave', () => { hovered = false; playback(); });
    listen(root, 'pointerdown', () => { interacting = true; playback(); });
    const release = () => { if (!interacting) return; interacting = false; clearTimeout(settleTimer); settleTimer = setTimeout(settled, 180); };
    listen(document, 'pointerup', release); listen(document, 'pointercancel', release);
    listen(viewport, 'scroll', () => { scrolling = true; playback(); clearTimeout(settleTimer); settleTimer = setTimeout(settled, 140); }, {passive: true});
    listen(viewport, 'scrollend', settled);
    const resize = () => { if (viewport.clientWidth === width) return; width = viewport.clientWidth; viewport.scrollTo({left: current * width, behavior: 'instant'}); };
    const sizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (sizeObserver) sizeObserver.observe(viewport); else listen(window, 'resize', resize);
    const visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .35; playback();
    }, {threshold: [0, .35]}) : null;
    visibilityObserver?.observe(viewport);
    listen(document, 'visibilitychange', playback);
    const dialogs = new MutationObserver(playback);
    dialogs.observe(document.body, {subtree: true, attributes: true, attributeFilter: ['open']});
    listen(reduced, 'change', () => { reset(); playback(); });
    select(0, false); playback();
    return () => { clearTimeout(settleTimer); cancelAnimationFrame(frame); sizeObserver?.disconnect(); visibilityObserver?.disconnect(); dialogs.disconnect(); };
  }

  class Homepage extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.listen = (el, type, fn, options = {}) => el.addEventListener(type, fn, {...options, signal});
      this.cleanCampaign = campaign(this.querySelector('.hero-carousel'), signal);
      this.listen(this, 'click', e => this.click(e));
      this.listen(this, 'click', e => this.beforeAdd(e), {capture: true});
      this.listen(this, 'keydown', e => this.keydown(e));
      this.listen(this, 'change', () => this.syncLooks());
      this.listen(window, 'ruemood:saved-changed', () => this.syncSaved());
      this.listen(document, 'DOMContentLoaded', () => this.syncSaved(), {once: true});
      this.querySelectorAll('.product-photo__alternate').forEach(img => {
        const ready = () => { if (img.naturalWidth > 0) img.classList.add('is-ready'); };
        if (img.complete) ready(); else this.listen(img, 'load', ready, {once: true});
      });
      this.querySelectorAll('[data-home-look-dialog]').forEach(dialog => {
        this.listen(dialog, 'cancel', e => { if (e.target === dialog) { e.preventDefault(); this.closeLook(dialog); } });
        this.listen(dialog, 'pointerdown', e => { dialog.backdropStart = e.target === dialog && this.outside(dialog, e); });
        this.listen(dialog, 'pointercancel', () => { dialog.backdropStart = false; });
        this.listen(dialog, 'click', e => { if (e.target === dialog && dialog.backdropStart && this.outside(dialog, e)) this.closeLook(dialog); dialog.backdropStart = false; });
        this.listen(dialog, 'close', e => { if (e.target === dialog) this.finishClose(dialog); });
      });
      this.lookObserver = new MutationObserver(() => this.syncLooks());
      this.querySelectorAll('[data-outfit-count],[data-outfit-status]').forEach(el => this.lookObserver.observe(el, {childList: true, characterData: true, subtree: true}));
      this.syncSaved(); this.waitForLooks(0); this.setupDiscovery();
    }
    disconnectedCallback() {
      this.querySelectorAll('[data-home-look-dialog][open]').forEach(dialog => this.closeLook(dialog));
      this.abort?.abort(); this.abort = null;
      this.discoveryObserver?.disconnect(); this.cleanCampaign?.(); this.lookObserver?.disconnect(); clearTimeout(this.readyTimer);
    }
    setupDiscovery() {
      const panels = [...this.querySelectorAll('[data-home-filter-panel]')];
      this.discoverySeen = new Set();
      this.discoveryObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const card = entry.target;
          if (!entry.isIntersecting || entry.intersectionRatio < .5 || document.hidden || document.querySelector('dialog[open]')) return;
          const panel = card.closest('[data-home-filter-panel]');
          if (panel.hidden || this.discoverySeen.has(card)) return;
          this.discoverySeen.add(card);
          this.trackDiscovery('view', panel, card);
        });
      }, {threshold: [.5]}) : null;
      panels.forEach(panel => this.updateDiscovery(panel));
      const state = history.state?.ruemoodHomeDiscovery;
      if (state?.version === 1 && state.section === this.id && state.counts && typeof state.counts === 'object') {
        panels.forEach(panel => {
          const count = Number(state.counts[panel.dataset.homeFilterPanel]);
          if (!Number.isFinite(count)) return;
          while (panel.querySelector('[data-home-products]').children.length < Math.min(count, 12) && panel.querySelector('template').content.children.length) this.moreDiscovery(panel, false);
        });
        const tab = [...this.querySelectorAll('[data-home-filter]')].find(t => t.dataset.homeFilter === state.tab);
        if (tab) this.selectTab(tab, 'filter');
        if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward' && Number.isFinite(state.scroll)) {
          const restore = () => requestAnimationFrame(() => window.scrollTo({top:state.scroll,behavior:'instant'}));
          if (document.readyState === 'complete') restore(); else this.listen(window,'load',restore,{once:true});
        }
      }
      this.discoveryReady = true;
      this.listen(window,'pagehide',()=>this.saveDiscovery());
      this.listen(this,'click',event=>{
        const link=event.target.closest('a');const panel=link?.closest('[data-home-filter-panel]');
        if (!panel) return;
        if(link.matches('.home-view-all')) this.trackDiscovery('view_all',panel);
        else if(link.closest('[data-home-card]')) this.trackDiscovery('select',panel,link.closest('[data-home-card]'));
      },{capture:true});
    }
    updateDiscovery(panel) {
      const grid=panel.querySelector('[data-home-products]'),template=panel.querySelector('[data-home-more-cards]');
      if(!grid || !template) return;
      panel.querySelector('[data-home-count]').textContent=grid.querySelectorAll('[data-home-card]').length+' pieces shown';
      panel.querySelector('[data-home-more]').hidden=template.content.children.length===0 || grid.children.length>=12;
      grid.querySelectorAll('[data-home-card]').forEach(card=>this.discoveryObserver?.observe(card));
    }
    moreDiscovery(panel, interactive) {
      const grid=panel.querySelector('[data-home-products]'),template=panel.querySelector('[data-home-more-cards]');
      const added=[...template.content.children].slice(0,Math.min(4,12-grid.children.length));
      if(!added.length) return;
      grid.append(...added);
      added.forEach(card=>card.querySelectorAll('.product-photo__alternate').forEach(img=>{
        const ready=()=>{if(img.naturalWidth>0)img.classList.add('is-ready');};
        if(img.complete) ready();else this.listen(img,'load',ready,{once:true});
      }));
      this.updateDiscovery(panel);this.syncSaved();
      if(interactive){
        // Keep the new row in view; keyboard users can continue through its products.
        const first=added[0].querySelector('.product-photo');
        first?.focus({preventScroll:true});
        added[0].scrollIntoView({block:'nearest',behavior:'instant'});
        this.saveDiscovery();this.trackDiscovery('more',panel);
      }
    }
    saveDiscovery() {
      const counts={};this.querySelectorAll('[data-home-filter-panel]').forEach(p=>{counts[p.dataset.homeFilterPanel]=p.querySelector('[data-home-products]')?.children.length||4;});
      try{history.replaceState({...history.state,ruemoodHomeDiscovery:{version:1,section:this.id,tab:this.querySelector('[data-home-filter][aria-selected="true"]')?.dataset.homeFilter,counts,scroll:window.scrollY}},'',location.href);}catch{}
    }
    trackDiscovery(action,panel,card) {
      try {
        const q=new URLSearchParams(location.search);
        if(window.RuemoodMeasurementConfig?.enabled!==true || window.RuemoodMeasurementConfig?.qa===true || window.Shopify?.designMode || q.has('preview_theme_id') || ['rm_pairing_qa','rm_analytics_qa','rm_shopping_preview'].some(k=>q.get(k)==='1') || sessionStorage.getItem('rm_measurement_qa')==='1')return;
        if(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()!==true || window.Shopify.customerPrivacy.saleOfDataAllowed?.()===false)return;
        const grid=panel.querySelector('[data-home-products]');
        const data={version:'home-discovery-v1',action,category:panel.dataset.homeCategory,shown:grid.children.length};
        if(card){data.product_id=card.dataset.homeProductId;data.position=[...grid.children].indexOf(card)+1;}
        const pending=window.Shopify.analytics?.publish?.('ruemood_home_discovery',data);pending?.catch?.(()=>{});
      }catch{}
    }

    waitForLooks(attempt) {
      if (!this.isConnected) return;
      const ready = [...this.querySelectorAll('.rm-home-look-pairings')].every(block => block.outfit);
      if (ready) { this.querySelectorAll('[data-home-look-loading]').forEach(el => { el.hidden = true; }); this.syncLooks(); return; }
      if (attempt < 40) this.readyTimer = setTimeout(() => this.waitForLooks(attempt + 1), 100);
      else this.querySelectorAll('[data-home-look-loading]').forEach(el => { el.textContent = 'Outfit options could not load. Open each product for its available options.'; });
    }
    outside(dialog, event) { const r = dialog.getBoundingClientRect(); return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom; }
    syncSaved() {
      if (!window.RuemoodShopping?.isSaved || !window.RuemoodShopping?.toggleSave) return;
      this.querySelectorAll('[data-home-save]').forEach(button => {
        const saved = window.RuemoodShopping.isSaved(button.dataset.homeSave);
        button.hidden = false; button.setAttribute('aria-pressed', String(saved));
        const title = button.closest('[data-home-card]').querySelector('.product-title').textContent.trim();
        button.setAttribute('aria-label', (saved ? 'Unsave ' : 'Save ') + title);
      });
    }
    syncLooks() {
      this.querySelectorAll('.rm-home-look-pairings').forEach(block => {
        if (!block.outfit || !block.cards) return;
        block.cards.forEach(card => {
          const included = block.outfit.selected.has(card.dataset.pairId);
          const unavailable = block.outfit.inBag.has(card.dataset.pairId) || !card.variants.some(v => v.available);
          card.classList.toggle('is-excluded', !included || unavailable);
          card.querySelectorAll('[data-home-option]').forEach(button => {
            const control = card.controls[Number(button.dataset.homeOption)];
            const option = [...(control?.options || [])].find(o => o.value === button.dataset.value);
            button.disabled = !included || unavailable || !!block.outfit.busy || !!block.outfit.uncertain || !option || option.disabled;
            button.setAttribute('aria-pressed', String(control?.value === button.dataset.value));
          });
          card.querySelectorAll('[data-home-option-group]').forEach(group => {
            const control = card.controls[Number(group.dataset.homeOptionGroup)];
            group.querySelector('[data-home-chosen]').textContent = control?.value ? ': ' + control.value : '';
          });
          if (card.outfitNote?.textContent === 'The piece you are viewing') card.outfitNote.textContent = '';
        });
      });
    }
    selectTab(button, kind) {
      const filter = kind === 'filter';
      const attr = filter ? 'homeFilter' : 'homeLookTab';
      const value = button.dataset[attr];
      this.querySelectorAll(filter ? '[data-home-filter]' : '[data-home-look-tab]').forEach(tab => {
        const selected = tab === button; tab.tabIndex = selected ? 0 : -1; tab.setAttribute('aria-selected', String(selected));
        if (filter) tab.setAttribute('aria-pressed', String(selected));
      });
      this.querySelectorAll(filter ? '[data-home-filter-panel]' : '[data-home-look-panel]').forEach(panel => { panel.hidden = panel.dataset[filter ? 'homeFilterPanel' : 'homeLookPanel'] !== value; });
      if (filter) {
        this.querySelector('[data-home-edit-title]').textContent = button.dataset.heading;
        const link = this.querySelector('[data-home-edit-link]');
        link.href = button.dataset.url; link.dataset.homeBrowse = button.dataset.browse;
        link.replaceChildren(document.createTextNode(button.dataset.linkLabel + ' '));
        const arrow = document.createElement('span'); arrow.setAttribute('aria-hidden', 'true'); arrow.textContent = '→'; link.append(arrow);
        if (this.discoveryReady) this.saveDiscovery();
      }
    }
    openLook(id, trigger, productId) {
      const dialog = [...this.querySelectorAll('[data-home-look-dialog]')].find(d => d.dataset.homeLookDialog === id);
      if (!dialog || dialog.open) return;
      dialog.homeTrigger = trigger; dialog.homeOverflow = document.body.style.overflow;
      dialog.backdropStart = false;
      if (typeof dialog.showModal !== 'function') { this.querySelector('[data-home-status]').textContent = 'Open a product to choose its options in this browser.'; return; }
      dialog.showModal(); document.body.style.overflow = 'hidden'; trigger?.setAttribute('aria-expanded', 'true');
      this.syncLooks();
      const card = productId && [...dialog.querySelectorAll('[data-pair-id]')].find(el => el.dataset.pairId === productId);
      const focus = card?.querySelector('[data-home-option]:not([disabled])') || dialog.querySelector('[data-home-look-close]');
      requestAnimationFrame(() => { if (!dialog.open) return; card?.scrollIntoView({block: 'start', behavior: 'instant'}); focus.focus({preventScroll: true}); });
    }
    closeLook(dialog) {
      if (!dialog?.open) return;
      dialog.querySelectorAll('.rm-quickshop[open]').forEach(child => child.closest('[data-pair-id]')?.quickshop?.close());
      dialog.close(); this.finishClose(dialog);
    }
    finishClose(dialog) {
      if (dialog.open || !Object.hasOwn(dialog, 'homeOverflow')) return;
      document.body.style.overflow = dialog.homeOverflow; delete dialog.homeOverflow;
      dialog.homeTrigger?.setAttribute('aria-expanded', 'false');
      if (dialog.homeTrigger?.isConnected) dialog.homeTrigger.focus({preventScroll: true});
    }
    beforeAdd(event) {
      const add = event.target.closest('[data-outfit-add]');
      if (!add || !this.contains(add)) return;
      const block = add.closest('.rm-home-look-pairings'), outfit = block?.outfit;
      if (!outfit || outfit.busy || outfit.uncertain) return;
      const card = outfit.pending().find(c => !c.variant?.available);
      if (!card) return;
      event.preventDefault(); event.stopPropagation();
      const names = card.controls.filter(control => !control.value).map(control => control.dataset.optionName.toLowerCase());
      const title = card.querySelector('.rm-pair__title').textContent.trim();
      block.querySelector('[data-outfit-status]').textContent = names.length ? 'Choose ' + names.join(' and ') + ' for ' + title + '.' : 'Choose an available combination for ' + title + '.';
      const missing = card.controls.findIndex(control => !control.value);
      const target = card.querySelector('[data-home-option="' + Math.max(0, missing) + '"]:not([disabled])');
      card.scrollIntoView({block: 'start', behavior: 'instant'}); target?.focus({preventScroll: true});
    }
    click(event) {
      const button = event.target.closest('button,a');
      if (!button || !this.contains(button) || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (button.hasAttribute('data-home-more')) { this.moreDiscovery(button.closest('[data-home-filter-panel]'), true); return; }
      if (button.dataset.homeFilter) { this.selectTab(button, 'filter'); return; }
      if (button.dataset.homeLookTab) { this.selectTab(button, 'look'); return; }
      if (button.dataset.homeFeaturedLook) {
        const tab = [...this.querySelectorAll('[data-home-look-tab]')].find(t => t.dataset.homeLookTab === button.dataset.homeFeaturedLook);
        if (tab) this.selectTab(tab, 'look'); return;
      }
      if (button.dataset.homeLookOpen) { event.preventDefault(); this.openLook(button.dataset.homeLookOpen, button, button.dataset.homeLookProduct); return; }
      if (button.hasAttribute('data-home-look-close')) { this.closeLook(button.closest('[data-home-look-dialog]')); return; }
      if (button.hasAttribute('data-outfit-bag') || button.hasAttribute('data-quick-bag')) { this.closeLook(button.closest('[data-home-look-dialog]')); return; }
      if (button.dataset.homeOption !== undefined) {
        const card = button.closest('[data-pair-id]'), control = card.controls?.[Number(button.dataset.homeOption)];
        if (!button.disabled && control) { control.value = button.dataset.value; control.dispatchEvent(new Event('change', {bubbles: true})); }
        return;
      }
      if (button.dataset.homeSave && window.RuemoodShopping?.toggleSave) {
        const ok = window.RuemoodShopping.toggleSave(button.dataset.homeSave); this.syncSaved();
        this.querySelector('[data-home-status]').textContent = ok ? (window.RuemoodShopping.isSaved(button.dataset.homeSave) ? 'Saved to My Ruemood.' : 'Removed from saved pieces.') : 'Your saved pieces could not be updated.';
        return;
      }
      if (button.dataset.homeQuickAdd && window.RuemoodShopping?.openProduct) { event.preventDefault(); window.RuemoodShopping.openProduct(button.dataset.homeQuickAdd, button); return; }
      if (button.dataset.homeBrowse && window.RuemoodShopping?.openBrowse) { event.preventDefault(); window.RuemoodShopping.openBrowse(button.dataset.homeBrowse, button); }
    }
    keydown(event) {
      const button = event.target.closest('[data-home-filter],[data-home-look-tab]');
      if (!button) return;
      const filter = button.hasAttribute('data-home-filter');
      const tabs = [...this.querySelectorAll(filter ? '[data-home-filter]' : '[data-home-look-tab]')];
      let index = tabs.indexOf(button);
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') index = (index + tabs.length - 1) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault(); tabs[index].focus(); this.selectTab(tabs[index], filter ? 'filter' : 'look');
    }
  }
  customElements.define('ruemood-homepage', Homepage);
})();
